/**
 * Fenris — per-order purchase limit ("max per order")
 * ===================================================
 *
 * WHAT THIS DOES
 * Stops a customer adding more of a product to the basket than the shop allows. The
 * limit is resolved in Liquid by `snippets/fenris-max-per-order.liquid` from the
 * product metafield `custom.max_per_order` (the lower of that and Shopify's native
 * `variant.quantity_rule.max` wins) and is handed to this file as a plain
 * `data-max-per-order` attribute on the markup.
 *
 * Nothing in this file is tied to a particular product or variant ID. Capping another
 * product later only needs the metafield set on that product — no code change.
 *
 * WHY THIS FILE EXISTS
 * The stock theme already respects the `max` attribute of the quantity <input>: the
 * product page stepper, the cart page stepper and the cart drawer stepper all stop at
 * it, and `product-form.js` refuses to submit when "quantity already in cart" plus
 * "quantity being added" would exceed it. Two paths had NO enforcement at all:
 *
 *   1. Quick-add / upsell buttons on product cards. `snippets/quick-add.liquid` posts a
 *      hidden quantity field and renders no quantity selector, so `product-form.js` has
 *      nothing to check against and happily adds one more on every click.
 *   2. Any add-to-cart made against a stale server-rendered cart count — a second tab,
 *      or after quick-adding the same product elsewhere on the page.
 *
 * This module is a single gate that every add-to-cart click passes through, and it
 * always re-reads the live basket from /cart.js before letting a click proceed.
 *
 * HOW IT WORKS
 * A capture-phase `click` listener on `document` catches the submit button of any
 * product form that carries a limit. It cancels that click, reads /cart.js, and then
 * either replays the click (flagged, so it passes straight back through this gate) or
 * shows an inline message and leaves the basket untouched.
 *
 * The listener is registered in the capture phase, and this script is loaded ahead of
 * the theme's own bundle in layout/theme.liquid, so it runs before the theme's
 * delegated click handler in `assets/component.js` and can stop it cleanly.
 *
 * SCOPE AND SAFETY
 * - A product form with no limit is never touched: the listener returns immediately, so
 *   every other product on the store behaves exactly as it did before.
 * - If /cart.js cannot be read the click is ALLOWED through rather than blocked, so a
 *   network blip can never make the shop unbuyable.
 * - The limit is enforced per variant, which is how the theme's own quantity input and
 *   the cart page stepper already behave.
 *
 * THIS IS NOT A SECURITY CONTROL
 * It is a storefront guard rail. A determined customer can still POST directly to
 * /cart/add.js and bypass it. Enforcing the cap for real requires a Shopify Function
 * (cart transform / checkout validation), which this store does not have.
 */

(function () {
  'use strict';

  /** How long the inline message stays on screen. Matches product-form.js. */
  var ERROR_DISPLAY_MS = 10000;

  /**
   * Set on a button just before we replay its click, so the replayed click is
   * recognised as already-validated and passes straight through the gate.
   * Becomes the `data-fenris-max-per-order-pass` attribute.
   */
  var PASS_FLAG = 'fenrisMaxPerOrderPass';

  /**
   * Reads the per-order limit that applies to a product form.
   *
   * Liquid publishes `data-max-per-order` on the <product-form-component> itself for
   * quick-add forms, and on the <quantity-selector-component> for the product page and
   * cart lines. Either location is accepted.
   *
   * @param {Element} form - a <product-form-component> element
   * @returns {number} the limit, or 0 when this product is unlimited
   */
  function resolveLimit(form) {
    var source = form.hasAttribute('data-max-per-order') ? form : form.querySelector('[data-max-per-order]');
    if (!source) return 0;

    var limit = parseInt(source.getAttribute('data-max-per-order'), 10);
    return isFinite(limit) && limit > 0 ? limit : 0;
  }

  /** @returns {string} the storefront cart JSON endpoint, honouring localised routes. */
  function cartUrl() {
    var base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
    return base + '.js';
  }

  /** @returns {Promise<Object>} the live cart, straight from the storefront. */
  function readCart() {
    return fetch(cartUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (response) {
      if (!response.ok) throw new Error('Unexpected /cart.js response: ' + response.status);
      return response.json();
    });
  }

  /**
   * @param {Object} cart - parsed /cart.js payload
   * @param {string} variantId - the variant about to be added
   * @returns {number} how many of that variant are already in the basket
   */
  function quantityInCart(cart, variantId) {
    if (!cart || !cart.items || !variantId) return 0;

    return cart.items.reduce(function (total, item) {
      return String(item.variant_id) === String(variantId) ? total + (Number(item.quantity) || 0) : total;
    }, 0);
  }

  /**
   * @param {Element} form
   * @param {string} name - form field name
   * @returns {string}
   */
  function fieldValue(form, name) {
    var field = form.querySelector('[name="' + name + '"]');
    return field ? field.value : '';
  }

  /** @returns {number} how many the customer is trying to add right now. */
  function requestedQuantity(form) {
    var value = parseInt(fieldValue(form, 'quantity'), 10);
    return isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * Builds the customer-facing message. The theme's locale string carries a literal
   * `{{ maximum }}` placeholder which product-form.js substitutes the same way, so the
   * wording stays translatable and identical across both code paths.
   *
   * @param {Element} form
   * @param {number} limit
   * @returns {string}
   */
  function buildMessage(form, limit) {
    var template =
      form.getAttribute('data-quantity-error-max') || 'You can only add {{ maximum }} of this item to your cart.';

    return template.split('{{ maximum }}').join(String(limit));
  }

  /**
   * Announces the message to screen readers via the form's existing live region.
   * @param {Element} form
   * @param {string} text
   */
  function announce(form, text) {
    var region = form.querySelector('[ref="liveRegion"]');
    if (!region) return;

    region.textContent = text;
    window.setTimeout(function () {
      if (region.textContent === text) region.textContent = '';
    }, ERROR_DISPLAY_MS);
  }

  /**
   * Shows the blocked-state message.
   *
   * On the product page we reuse the theme's own error slot so the message looks and
   * sits exactly where a native quantity-rule error would. Quick-add buttons on product
   * cards have no such slot, so we render a small bubble over the card instead.
   *
   * @param {Element} form
   * @param {string} text
   */
  function showMessage(form, text) {
    announce(form, text);

    var nativeSlot = form.querySelector('[ref="addToCartTextError"]');

    if (nativeSlot) {
      // Clear anything the theme may have written straight into the slot, so the
      // message can never end up doubled.
      Array.prototype.slice.call(nativeSlot.childNodes).forEach(function (node) {
        if (node.nodeType === 3) node.textContent = '';
      });

      var slotText = nativeSlot.querySelector('.fenris-max-per-order-text');
      if (!slotText) {
        slotText = document.createElement('span');
        slotText.className = 'fenris-max-per-order-text';
        nativeSlot.appendChild(slotText);
      }
      slotText.textContent = text;
      nativeSlot.classList.remove('hidden');

      window.clearTimeout(nativeSlot.fenrisHideTimer);
      nativeSlot.fenrisHideTimer = window.setTimeout(function () {
        nativeSlot.classList.add('hidden');
      }, ERROR_DISPLAY_MS);

      return;
    }

    var host = form.closest('quick-add-component') || form;
    var bubble = host.querySelector('.fenris-max-per-order-message');

    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'fenris-max-per-order-message';
      bubble.setAttribute('role', 'alert');
      host.appendChild(bubble);
    }

    bubble.textContent = text;
    bubble.removeAttribute('hidden');

    window.clearTimeout(bubble.fenrisHideTimer);
    bubble.fenrisHideTimer = window.setTimeout(function () {
      bubble.setAttribute('hidden', '');
    }, ERROR_DISPLAY_MS);
  }

  /**
   * Feeds the freshly read cart count back into the theme's quantity selector, so the
   * theme's own max check in product-form.js agrees with ours instead of working from
   * a server-rendered number that may be stale.
   *
   * @param {Element} form
   * @param {number} inCart
   */
  function syncQuantitySelector(form, inCart) {
    var selector = form.querySelector('quantity-selector-component');
    if (!selector || typeof selector.setCartQuantity !== 'function') return;

    try {
      selector.setCartQuantity(inCart);
    } catch (error) {
      // Non-fatal: the gate below is the thing that actually protects the limit.
      console.warn('[fenris-max-per-order] could not sync the quantity selector', error);
    }
  }

  /**
   * Replays a click that we have already validated.
   * @param {HTMLElement} button
   */
  function replay(button) {
    button.dataset[PASS_FLAG] = 'true';
    button.click();
  }

  /**
   * @param {Element} form
   * @param {HTMLElement} button
   * @param {number} limit
   */
  function validateAndReplay(form, button, limit) {
    // Read what the customer asked for BEFORE touching the quantity selector, because
    // syncing it can clamp the input value.
    var variantId = fieldValue(form, 'id');
    var requested = requestedQuantity(form);

    readCart().then(
      function (cart) {
        var inCart = quantityInCart(cart, variantId);

        syncQuantitySelector(form, inCart);

        if (inCart + requested > limit) {
          showMessage(form, buildMessage(form, limit));
          return;
        }

        replay(button);
      },
      function (error) {
        // Fail open on purpose — see the note at the top of this file.
        console.error('[fenris-max-per-order] could not read the basket, allowing the add', error);
        replay(button);
      }
    );
  }

  /**
   * The gate. Runs in the capture phase, before the theme's own delegated handlers.
   * @param {Event} event
   */
  function onClickCapture(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    // Only real add-to-cart submit buttons. The quick-add "Choose options" button is
    // type="button" and must keep opening the modal, so it is deliberately not matched.
    var button = target.closest('button[type="submit"][name="add"]');
    if (!button) return;

    var form = button.closest('product-form-component');
    if (!form) return;

    var limit = resolveLimit(form);
    if (!limit) return; // Unlimited product: hands off, behave exactly as before.

    if (button.dataset[PASS_FLAG] === 'true') {
      delete button.dataset[PASS_FLAG];
      return; // Our own replayed click.
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    validateAndReplay(form, button, limit);
  }

  document.addEventListener('click', onClickCapture, true);
})();
